import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AuthContext } from "../../context/authContext";
import api from "../../Api";
import Cookies from "js-cookie";

const Login = () => {
  const { refreshUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    mobile: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Override body background for this page
  useEffect(() => {
    const originalBackground = document.body.style.background;
    document.body.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)';
    return () => {
      document.body.style.background = originalBackground;
    };
  }, []);

  const navigate = useNavigate();

  const mobileRegex = /^\d{8}$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const validateForm = () => {
    const errors = {};

    if (!formData.mobile) {
      errors.mobile = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      errors.mobile = "Please enter a valid 8-digit mobile number";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (!passwordRegex.test(formData.password)) {
      errors.password =
        "Password must be at least 8 characters with uppercase, lowercase, number and special character";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      try {
        const res = await api.post("/classBooking/api/auth/login", formData, {
          withCredentials: true,
        });

        if (res.status !== 200) {
          setFormErrors(res.data);
        } else {
          refreshUser();
          
          const activeCourseCookie = Cookies.get('activeCourse');
          if (activeCourseCookie) {
            navigate('/en/classconfirmpayment');
          } else {
            navigate('/en/');
          }
        }
      } catch (err) {
        console.log(err.response?.data);
        const errData = err.response?.data;
        const message = typeof errData === 'object' ? Object.values(errData)[0] : errData || "Login failed. Please try again.";
        setFormErrors({ general: message });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <PageContainer>
      <LoginCard>
        <LogoSection>
          <WelcomeText>Welcome back</WelcomeText>
          <SubText>Sign in to continue booking</SubText>
        </LogoSection>

        <FormSection onSubmit={handleSubmit}>
          <InputGroup>
            <InputLabel>Mobile Number</InputLabel>
            <InputWrapper $hasError={formErrors.mobile}>
              <InputIcon>📱</InputIcon>
              <StyledInput
                type="tel"
                placeholder="Enter your 8-digit mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                maxLength={8}
              />
            </InputWrapper>
            {formErrors.mobile && <ErrorText>{formErrors.mobile}</ErrorText>}
          </InputGroup>

          <InputGroup>
            <InputLabel>Password</InputLabel>
            <InputWrapper $hasError={formErrors.password}>
              <InputIcon>🔒</InputIcon>
              <StyledInput
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <TogglePassword type="button" onClick={() => setShowPassword(!showPassword)}>
                👁️
              </TogglePassword>
            </InputWrapper>
            {formErrors.password && <ErrorText>{formErrors.password}</ErrorText>}
          </InputGroup>

          {formErrors.general && <GeneralError>{formErrors.general}</GeneralError>}

          <LoginButton type="submit" disabled={isLoading}>
            {isLoading ? <Spinner /> : "Sign In"}
          </LoginButton>

          <ForgotLink to="/en/resetpassword">Login with SMS instead?</ForgotLink>
        </FormSection>

        <Divider>
          <DividerLine />
          <DividerText>or</DividerText>
          <DividerLine />
        </Divider>

        <SignUpSection>
          <SignUpText>Don't have an account?</SignUpText>
          <SignUpLink to="/en/signup">Create Account</SignUpLink>
        </SignUpSection>
      </LoginCard>

      <PoweredByFooter>
        <div>
          Powered by <img src="/dynamic_logo.svg" alt="DMG Logo" /> | booking.dynevents.com
        </div>
      </PoweredByFooter>
    </PageContainer>
  );
};

export default Login;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-bottom: 60px;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const WelcomeText = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SubText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: #888;
  margin: 0;
`;

const FormSection = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${props => props.$hasError ? '#fff5f5' : '#f8f9fa'};
  border: 2px solid ${props => props.$hasError ? '#ff6b6b' : 'transparent'};
  border-radius: 14px;
  transition: all 0.2s ease;
  overflow: hidden;

  &:focus-within {
    background: white;
    border-color: ${props => props.$hasError ? '#ff6b6b' : '#1a73e8'};
    box-shadow: 0 0 0 4px ${props => props.$hasError ? 'rgba(255, 107, 107, 0.1)' : 'rgba(26, 115, 232, 0.1)'};
  }
`;

const InputIcon = styled.span`
  font-size: 18px;
  opacity: 0.7;
  flex-shrink: 0;
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: #333;
  outline: none;

  &::placeholder {
    color: #aaa;
  }
`;

const TogglePassword = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`;

const ErrorText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: #ff6b6b;
  padding-left: 4px;
`;

const GeneralError = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #ff6b6b;
  text-align: center;
  padding: 12px;
  background: #fff5f5;
  border-radius: 10px;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ForgotLink = styled(Link)`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #666;
  text-align: center;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #1a73e8;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 28px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e8e8e8;
`;

const DividerText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #999;
`;

const SignUpSection = styled.div`
  text-align: center;
`;

const SignUpText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
`;

const SignUpLink = styled(Link)`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a73e8;
  text-decoration: none;
  margin-left: 6px;
  transition: color 0.2s;

  &:hover {
    color: #0d5bba;
  }
`;

const PoweredByFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 36px;
  font-size: 12px;
  font-family: 'Poppins', sans-serif;
  color: #666;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  
  @media (min-width: 500px) {
    width: 500px;
    left: calc((100vw - 500px) / 2);
    right: calc((100vw - 500px) / 2);
  }

  img {
    display: inline-block;
    width: 20px;
    vertical-align: middle;
    margin: 0 4px;
  }
`;
